import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../styles/Sidebar.css';
import { FiLogOut, FiCalendar, FiUser, FiHome, FiFileText, FiChevronLeft, FiChevronRight, FiHelpCircle, FiMessageSquare } from 'react-icons/fi';
import { PiDog } from "react-icons/pi";
import { AiOutlineTeam, AiOutlineCheck } from "react-icons/ai";
import { MdOutlineTask, MdSchool } from "react-icons/md";
import { BsClipboardCheck } from "react-icons/bs";
import LanguageSwitcher from '../commons/LanguageSwitcher';

const TrainerSidebar = ({ trainer, isCollapsed, onToggleCollapse, activeRoute }) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();

    // Helper function to check if route is active
    const isRouteActive = (route) => {
        if (Array.isArray(route)) {
            return route.some(r => activeRoute?.includes(r));
        }
        return activeRoute?.includes(route);
    };

    const handleLogout = () => {
        localStorage.removeItem('jwt');
        localStorage.removeItem('refreshToken');
        navigate('/');
    };

    // Display trainer's name
    const displayName = trainer?.firstName && trainer?.lastName
        ? `${trainer.firstName} ${trainer.lastName}`
        : trainer?.username || 'Trainer';

    return (
        <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            {/* Collapse/Expand Toggle Button */}
            <button
                className="collapse-toggle"
                onClick={onToggleCollapse}
                title={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
            >
                {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            </button>

            {!isCollapsed && (
                <>
                    <div className="profile-section">
                        <div className="profile-icon">
                            <span>{displayName.charAt(0)}</span>
                        </div>
                        <p>{displayName}</p>
                    </div>
                    <nav className="nav-section">
                        <ul>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/dashboard') ? 'active' : ''}`}
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <FiHome /> {t('sidebar.dashboard')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/podopieczni') ? 'active' : ''}`}
                                    onClick={() => navigate('/podopieczni')}
                                    data-onboarding="pets"
                                >
                                    <PiDog /> {t('sidebar.pets')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/owners') ? 'active' : ''}`}
                                    onClick={() => navigate('/owners/all')}
                                    data-onboarding="owners"
                                >
                                    <AiOutlineTeam /> {t('sidebar.owners')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/exercises') ? 'active' : ''}`}
                                    onClick={() => navigate('/exercises')}
                                    data-onboarding="exercises"
                                >
                                    <MdOutlineTask /> {t('sidebar.exercises')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/reports') ? 'active' : ''}`}
                                    onClick={() => navigate('/reports')}
                                    data-onboarding="reports"
                                >
                                    <FiFileText /> {t('sidebar.reports')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/survey') ? 'active' : ''}`}
                                    onClick={() => navigate(`/survey/school/${trainer?.schoolId}/all`)}
                                    data-onboarding="surveys"
                                >
                                    <BsClipboardCheck /> {t('sidebar.surveys')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/calendar') ? 'active' : ''}`}
                                    onClick={() => navigate('/calendar')}
                                    data-onboarding="calendar"
                                >
                                    <FiCalendar /> {t('sidebar.calendar')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/trainings/confirmed') ? 'active' : ''}`}
                                    onClick={() => navigate('/trainings/confirmed')}
                                    data-onboarding="trainings"
                                >
                                    <AiOutlineCheck /> {t('sidebar.trainings')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive(['/trainer/groups', '/trainer/group']) ? 'active' : ''}`}
                                    onClick={() => navigate('/trainer/groups')}
                                    data-onboarding="groups"
                                >
                                    <AiOutlineTeam /> {t('sidebar.groups')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/chat') ? 'active' : ''}`}
                                    onClick={() => navigate('/chat')}
                                >
                                    <FiMessageSquare /> {t('sidebar.chat')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/school/details') ? 'active' : ''}`}
                                    onClick={() => navigate('/school/details')}
                                    data-onboarding="school"
                                >
                                    <MdSchool /> {t('sidebar.school')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/trainer/account') ? 'active' : ''}`}
                                    onClick={() => navigate('/trainer/account')}
                                    data-onboarding="my-account"
                                >
                                    <FiUser /> {t('sidebar.account')}
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`nav-button ${isRouteActive('/trainer/help') ? 'active' : ''}`}
                                    onClick={() => navigate('/trainer/help')}
                                >
                                    <FiHelpCircle /> {t('sidebar.help')}
                                </button>
                            </li>
                        </ul>
                    </nav>
                    <LanguageSwitcher variant="sidebar" />
                    <button className="logout-button" onClick={handleLogout}>
                        <FiLogOut className="logout-icon" /> {t('sidebar.logout')}
                    </button>
                </>
            )}

            {/* Collapsed state - only show icons */}
            {isCollapsed && (
                <nav className="nav-section-collapsed">
                    <ul>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/dashboard') ? 'active' : ''}`}
                                onClick={() => navigate('/dashboard')}
                                title={t('sidebar.dashboard')}
                            >
                                <FiHome />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/podopieczni') ? 'active' : ''}`}
                                onClick={() => navigate('/podopieczni')}
                                title={t('sidebar.pets')}
                                data-onboarding="pets"
                            >
                                <PiDog />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/owners') ? 'active' : ''}`}
                                onClick={() => navigate('/owners/all')}
                                title={t('sidebar.owners')}
                                data-onboarding="owners"
                            >
                                <AiOutlineTeam />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/exercises') ? 'active' : ''}`}
                                onClick={() => navigate('/exercises')}
                                title={t('sidebar.exercises')}
                                data-onboarding="exercises"
                            >
                                <MdOutlineTask />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/reports') ? 'active' : ''}`}
                                onClick={() => navigate('/reports')}
                                title={t('sidebar.reports')}
                                data-onboarding="reports"
                            >
                                <FiFileText />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/survey') ? 'active' : ''}`}
                                onClick={() => navigate(`/survey/school/${trainer?.schoolId}/all`)}
                                title={t('sidebar.surveys')}
                                data-onboarding="surveys"
                            >
                                <BsClipboardCheck />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/calendar') ? 'active' : ''}`}
                                onClick={() => navigate('/calendar')}
                                title={t('sidebar.calendar')}
                                data-onboarding="calendar"
                            >
                                <FiCalendar />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/trainings/confirmed') ? 'active' : ''}`}
                                onClick={() => navigate('/trainings/confirmed')}
                                title={t('sidebar.trainings')}
                                data-onboarding="trainings"
                            >
                                <AiOutlineCheck />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive(['/trainer/groups', '/trainer/group']) ? 'active' : ''}`}
                                onClick={() => navigate('/trainer/groups')}
                                title={t('sidebar.groups')}
                                data-onboarding="groups"
                            >
                                <AiOutlineTeam />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/chat') ? 'active' : ''}`}
                                onClick={() => navigate('/chat')}
                                title={t('sidebar.chat')}
                            >
                                <FiMessageSquare />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/school/details') ? 'active' : ''}`}
                                onClick={() => navigate('/school/details')}
                                title={t('sidebar.school')}
                                data-onboarding="school"
                            >
                                <MdSchool />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/trainer/account') ? 'active' : ''}`}
                                onClick={() => navigate('/trainer/account')}
                                title={t('sidebar.account')}
                                data-onboarding="my-account"
                            >
                                <FiUser />
                            </button>
                        </li>
                        <li>
                            <button
                                className={`nav-button-collapsed ${isRouteActive('/trainer/help') ? 'active' : ''}`}
                                onClick={() => navigate('/trainer/help')}
                                title={t('sidebar.help')}
                            >
                                <FiHelpCircle />
                            </button>
                        </li>
                    </ul>
                    <LanguageSwitcher variant="collapsed" />
                    <button
                        className="logout-button-collapsed"
                        onClick={handleLogout}
                        title={t('sidebar.logout')}
                    >
                        <FiLogOut />
                    </button>
                </nav>
            )}
        </aside>
    );
};

export default TrainerSidebar;
